/**
 * CallFoundry Service Worker Update Coordinator
 * 
 * Enforces ADR-0010: PWA Update Strategy with In-Flight Simulation Protection.
 * Prevents disruptive page reloads while a learner is engaged in an active call simulation or drill.
 */

type UpdateCallback = (reloadPage: () => Promise<void>) => void;

export class UpdateCoordinator {
  private isSimulationInProgress = false;
  private isRecordingInProgress = false;
  private pendingUpdate: (() => Promise<void>) | null = null;
  private listeners: Set<UpdateCallback> = new Set();

  /**
   * Set by Call Simulator and Drill components to shield the user from update reloads.
   */
  public setSimulationActive(active: boolean): void {
    this.isSimulationInProgress = active;
    this.checkPendingDeferred();
  }

  /**
   * Set by AudioRecorderService to shield active microphone capture from update reloads.
   */
  public setRecordingActive(active: boolean): void {
    this.isRecordingInProgress = active;
    this.checkPendingDeferred();
  }

  public isSimulationActive(): boolean {
    return this.isSimulationInProgress;
  }

  public isRecordingActive(): boolean {
    return this.isRecordingInProgress;
  }

  /**
   * Returns true if any high-value in-flight user activity is underway
   * that strictly prohibits disruptive service worker reloads.
   */
  public isProtected(): boolean {
    return this.isSimulationInProgress || this.isRecordingInProgress;
  }

  public hasPendingDeferredUpdate(): boolean {
    return this.pendingUpdate !== null;
  }

  public getPendingUpdate(): (() => Promise<void>) | null {
    return this.pendingUpdate;
  }

  /**
   * Called when the Service Worker registers a new waiting version.
   */
  public registerPendingUpdate(reloadPage: () => Promise<void>): void {
    if (this.isProtected()) {
      // Defer update until the active activity completes
      this.pendingUpdate = reloadPage;
    } else {
      this.notifyListeners(reloadPage);
    }
  }

  /**
   * Subscribe UI banners or notification pills to pending update events.
   */
  public onUpdateAvailable(callback: UpdateCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private checkPendingDeferred(): void {
    // If all protected activities concluded and an update was deferred, notify listeners now
    if (!this.isProtected() && this.pendingUpdate) {
      const updateToNotify = this.pendingUpdate;
      this.pendingUpdate = null;
      this.notifyListeners(updateToNotify);
    }
  }

  private notifyListeners(reloadPage: () => Promise<void>): void {
    for (const listener of this.listeners) {
      try {
        listener(reloadPage);
      } catch (err) {
        console.error('[PWA UpdateCoordinator] Error notifying listener:', err);
      }
    }
  }

  /**
   * Test/reset helper for unit testing lifecycle states.
   */
  public reset(): void {
    this.isSimulationInProgress = false;
    this.isRecordingInProgress = false;
    this.pendingUpdate = null;
    this.listeners.clear();
  }
}

export const updateCoordinator = new UpdateCoordinator();

